import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDayInTz, endOfDayInTz, formatDateInputInTz } from '@/utils/timezone';
import { getComparisonPeriod, type ComparisonPeriod } from '@/utils/periodComparison';
import { calculateCompletionRate } from '@/utils/dateFieldStandards';
import { useVisibility } from '@/hooks/useVisibility';

export interface CompanyOverviewMetrics {
  totalRevenue: number;
  totalSales: number;
  avgTicket: number;
  totalLeads: number;
  bookedLeads: number;
  callBookingRate: number;
  scheduledJobs: number;
  completedOfScheduled: number;
  jobConversionRate: number;
  openJobs: number;
  totalCancellations: number;
  revenueAtRisk: number;
  cancellationReasons: { reason: string; count: number; percentage: number }[];
}

export interface CompanyOverviewData {
  current: CompanyOverviewMetrics;
  previous?: CompanyOverviewMetrics;
  comparisonPeriod?: ComparisonPeriod;
}

// Jobs that have neither been finished nor called off — still need work done.
const OPEN_JOB_STATUSES = ['scheduled', 'in_progress', 'on_hold', 'quote_sent'];

// Date-field standards (src/utils/dateFieldStandards.ts):
// revenue by job_date, job counts by scheduled_date, leads by created_at,
// cancellations by cancelled_at.
const fetchOverview = async (
  from: Date,
  to: Date,
  timezone: string
): Promise<CompanyOverviewMetrics> => {
  const startISO = startOfDayInTz(from, timezone).toISOString();
  const endISO = endOfDayInTz(to, timezone).toISOString();
  const fromDateStr = formatDateInputInTz(from, timezone);
  const toDateStr = formatDateInputInTz(to, timezone);

  const [completedRes, scheduledRes, cancelledRes, leadsRes] = await Promise.all([
    supabase
      .from('jobs')
      .select('id, invoice_total')
      .eq('status', 'completed')
      .gte('job_date', fromDateStr)
      .lte('job_date', toDateStr),
    supabase
      .from('jobs')
      .select('id, status')
      .gte('scheduled_date', startISO)
      .lte('scheduled_date', endISO),
    supabase
      .from('jobs')
      .select('id, quoted_price, cancellation_reason')
      .eq('status', 'cancelled')
      .gte('cancelled_at', startISO)
      .lte('cancelled_at', endISO),
    supabase
      .from('leads')
      .select('id, status, job_id')
      .gte('created_at', startISO)
      .lte('created_at', endISO),
  ]);

  if (completedRes.error) throw completedRes.error;
  if (scheduledRes.error) throw scheduledRes.error;
  if (cancelledRes.error) throw cancelledRes.error;
  if (leadsRes.error) throw leadsRes.error;

  const completedJobs = completedRes.data || [];
  const totalRevenue = completedJobs.reduce((sum, j) => sum + (j.invoice_total || 0), 0);
  const totalSales = completedJobs.length;
  const avgTicket = totalSales > 0 ? totalRevenue / totalSales : 0;

  const scheduled = scheduledRes.data || [];
  const scheduledJobs = scheduled.length;
  const completedOfScheduled = scheduled.filter((j) => j.status === 'completed').length;
  const jobConversionRate = calculateCompletionRate(completedOfScheduled, scheduledJobs);
  const openJobs = scheduled.filter((j) => OPEN_JOB_STATUSES.includes(j.status)).length;

  const cancelled = cancelledRes.data || [];
  const totalCancellations = cancelled.length;
  const revenueAtRisk = cancelled.reduce((sum, j) => sum + (j.quoted_price || 0), 0);
  const reasonCounts: Record<string, number> = {};
  cancelled.forEach((j) => {
    const reason = j.cancellation_reason || 'Unknown';
    reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
  });
  const cancellationReasons = Object.entries(reasonCounts)
    .map(([reason, count]) => ({
      reason,
      count,
      percentage: totalCancellations > 0 ? (count / totalCancellations) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const leads = (leadsRes.data || []).filter((l) => l.status !== 'spam');
  const totalLeads = leads.length;
  const bookedLeads = leads.filter((l) => l.status === 'converted' && l.job_id !== null).length;
  const callBookingRate = totalLeads > 0 ? (bookedLeads / totalLeads) * 100 : 0;

  return {
    totalRevenue,
    totalSales,
    avgTicket,
    totalLeads,
    bookedLeads,
    callBookingRate,
    scheduledJobs,
    completedOfScheduled,
    jobConversionRate,
    openJobs,
    totalCancellations,
    revenueAtRisk,
    cancellationReasons,
  };
};

export const useCompanyOverview = (
  dateFrom: Date,
  dateTo: Date,
  timezone: string,
  compareEnabled: boolean
) => {
  const { canSeeCompanyFinancials } = useVisibility();
  return useQuery<CompanyOverviewData>({
    enabled: canSeeCompanyFinancials,
    queryKey: [
      'company-overview',
      dateFrom.toISOString(),
      dateTo.toISOString(),
      timezone,
      compareEnabled,
    ],
    queryFn: async () => {
      if (!compareEnabled) {
        return { current: await fetchOverview(dateFrom, dateTo, timezone) };
      }
      const comparisonPeriod = getComparisonPeriod(dateFrom, dateTo);
      const [current, previous] = await Promise.all([
        fetchOverview(dateFrom, dateTo, timezone),
        fetchOverview(comparisonPeriod.start, comparisonPeriod.end, timezone),
      ]);
      return { current, previous, comparisonPeriod };
    },
    staleTime: 60_000,
  });
};
